import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, TrendingUp, Flame, Snowflake, CircleDot, XCircle, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUTS = ['Chaud', 'Tiède', 'Froid', 'Perdu', 'Gagné'];

const getStatusIcon = (statut) => {
  const icons = {
    'Chaud': <Flame className="w-4 h-4" />,
    'Tiède': <CircleDot className="w-4 h-4" />,
    'Froid': <Snowflake className="w-4 h-4" />,
    'Perdu': <XCircle className="w-4 h-4" />,
    'Gagné': <CheckCircle className="w-4 h-4" />
  };
  return icons[statut];
};

const getStatusColor = (statut) => {
  const colors = {
    'Chaud': 'bg-red-100 text-red-800 border-red-200',
    'Tiède': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Froid': 'bg-blue-100 text-blue-800 border-blue-200',
    'Perdu': 'bg-gray-100 text-gray-800 border-gray-200',
    'Gagné': 'bg-green-100 text-green-800 border-green-200'
  };
  return colors[statut];
};

const Opportunites = () => {
  const navigate = useNavigate();
  const [opportunites, setOpportunites] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    contact_id: '',
    montant_estime: '',
    statut: 'Tiède',
    zone_geographique: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [oppResponse, contactsResponse] = await Promise.all([
        axios.get(`${API}/opportunites`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { withCredentials: true })
      ]);
      setOpportunites(oppResponse.data);
      setContacts(contactsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_id || !formData.montant_estime) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/opportunites`, {
        ...formData,
        montant_estime: parseFloat(formData.montant_estime)
      }, {
        withCredentials: true
      });
      toast.success('Opportunité créée avec succès');
      setDialogOpen(false);
      fetchData();
      setFormData({
        contact_id: '',
        montant_estime: '',
        statut: 'Tiède',
        zone_geographique: ''
      });
    } catch (error) {
      console.error('Error creating opportunite:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const groupedOpportunites = STATUTS.reduce((acc, statut) => {
    acc[statut] = opportunites.filter(opp => opp.statut === statut);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002255]"></div>
      </div>
    );
  }

  return (
    <div data-testid="opportunites-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#002255] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Opportunités
          </h1>
          <p className="text-gray-600">Suivez vos opportunités commerciales</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white"
              data-testid="add-opportunite-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Opportunité
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une Opportunité</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="contact_id">Contact *</Label>
                <Select 
                  value={formData.contact_id} 
                  onValueChange={(value) => setFormData(prev => ({...prev, contact_id: value}))}
                >
                  <SelectTrigger data-testid="select-contact">
                    <SelectValue placeholder="Sélectionnez un contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(contact => (
                      <SelectItem key={contact.contact_id} value={contact.contact_id}>
                        {contact.nom} {contact.prenoms}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="montant_estime">Montant Estimé (FCFA) *</Label>
                <Input
                  id="montant_estime"
                  type="number"
                  value={formData.montant_estime}
                  onChange={(e) => setFormData(prev => ({...prev, montant_estime: e.target.value}))}
                  placeholder="5000000"
                  required
                  data-testid="input-montant"
                />
              </div>

              <div>
                <Label htmlFor="statut">Statut</Label>
                <Select 
                  value={formData.statut} 
                  onValueChange={(value) => setFormData(prev => ({...prev, statut: value}))}
                >
                  <SelectTrigger data-testid="select-statut">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUTS.map(statut => (
                      <SelectItem key={statut} value={statut}>{statut}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="zone_geographique">Zone Géographique</Label>
                <Input
                  id="zone_geographique"
                  value={formData.zone_geographique}
                  onChange={(e) => setFormData(prev => ({...prev, zone_geographique: e.target.value}))}
                  placeholder="Abidjan, Cocody"
                  data-testid="input-zone"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#002255] hover:bg-[#002255]/90"
                data-testid="submit-opportunite-button"
              >
                Créer l'opportunité
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {STATUTS.map(statut => {
          const opps = groupedOpportunites[statut] || [];
          return (
            <div key={statut} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <Badge className={`${getStatusColor(statut)} border flex items-center gap-1`}>
                  {getStatusIcon(statut)}
                  {statut}
                </Badge>
                <span className="text-sm text-gray-500">({opps.length})</span>
              </div>
              
              <div className="space-y-3">
                {opps.map(opp => {
                  const contact = contacts.find(c => c.contact_id === opp.contact_id);
                  return (
                    <div
                      key={opp.opportunite_id}
                      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200/40 hover:shadow-md transition-all cursor-pointer"
                      data-testid={`opportunite-card-${opp.opportunite_id}`}
                    >
                      <p className="font-semibold text-[#002255] mb-2">
                        {contact ? `${contact.nom} ${contact.prenoms}` : 'Contact inconnu'}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        {new Intl.NumberFormat('fr-FR', {
                          style: 'currency',
                          currency: 'XOF',
                          minimumFractionDigits: 0
                        }).format(opp.montant_estime)}
                      </p>
                      {opp.zone_geographique && (
                        <p className="text-xs text-gray-500">{opp.zone_geographique}</p>
                      )}
                    </div>
                  );
                })}
                {opps.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">Aucune opportunité</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Opportunites;
